import { useQueries } from "@tanstack/react-query";

import { useCurrentOrg } from "@/providers/auth/hooks";

import type { EventModel } from "../OrgRecord";

import { useOrg, useOrgQueryOptions } from "./useOrg";

/**
 * The event model of `orgId`, defaulting to the current org, or `undefined`
 * while its record loads (or when there is no current org).
 *
 * Read from the org's fetched record rather than the current org kept in
 * storage: an org's event model changes as its events migrate, and the stored
 * record keeps whatever it held when the user picked the org.
 */
export function useOrgEventModel(orgId?: string): EventModel | undefined {
  const currentOrgId = useCurrentOrg()?.org_id;
  const { data } = useOrg(orgId ?? currentOrgId);
  if (data === undefined) {
    return undefined;
  }
  return data.event_model ?? "legacy";
}

/**
 * The event model of each org in `orgIds`, by org ID. An org whose record has
 * not loaded has no entry.
 *
 * For a view over data from several orgs, where each record's own org, not
 * the current org, decides what the server accepts.
 */
export function useOrgEventModels(
  orgIds: readonly string[],
): Readonly<Partial<Record<string, EventModel>>> {
  const orgQueryOptions = useOrgQueryOptions();
  return useQueries({
    queries: orgIds.map((orgId) => orgQueryOptions(orgId)),
    // A plain object, not a Map: TanStack Query reuses the previous `combine`
    // result when a deep comparison finds it equal, but compares only plain
    // objects and arrays that way. The object therefore keeps its identity
    // across renders until an org's event model changes, so callers can use it
    // as a memo dependency.
    combine: (results) =>
      Object.fromEntries(
        results.flatMap((result, index): Array<[string, EventModel]> =>
          result.data === undefined
            ? []
            : [[orgIds[index], result.data.event_model ?? "legacy"]],
        ),
      ),
  });
}
